// Shared bits of the admin usage-history page (/admin-point-usage.html) that
// the public map also needs: building the deep link and deciding whether the
// visitor is signed in. Kept free of Amplify and admin imports so the public
// map bundle stays small.

/** Backend PointKinds values the usage endpoints accept. */
export type UsagePointKind = 'PowerStation' | 'IndirectStation' | 'ChargingStation' | 'WaterPoint';

const USAGE_KINDS: readonly string[] = ['PowerStation', 'IndirectStation', 'ChargingStation', 'WaterPoint'];

/**
 * Map-side kind → backend kind, keyed lower-case: the public map uses
 * PascalCase kinds, the admin map lower-case ones. Both maps split charging
 * stations into AC and DC; the backend stores them as one ChargingStation kind.
 */
const MAP_KIND_TO_USAGE_KIND: Record<string, UsagePointKind> = {
  powerstation: 'PowerStation',
  indirectstation: 'IndirectStation',
  chargingstation: 'ChargingStation',
  acchargingstation: 'ChargingStation',
  dcchargingstation: 'ChargingStation',
  waterpoint: 'WaterPoint',
};

export function toUsageKind(mapKind: string): UsagePointKind | null {
  return MAP_KIND_TO_USAGE_KIND[mapKind.toLowerCase()] ?? null;
}

/** Deep link to one point on the usage-history page, or null for kinds it doesn't cover. */
export function usageHref(mapKind: string, id: number): string | null {
  const kind = toUsageKind(mapKind);
  return kind ? `/admin-point-usage.html?point=${kind}:${id}` : null;
}

/** Parses the page's `?point=Kind:Id` parameter. */
export function parsePointParam(value: string | null): { kind: UsagePointKind; id: number } | null {
  if (!value) return null;
  const match = /^([A-Za-z]+):(\d+)$/.exec(value);
  if (!match || !USAGE_KINDS.includes(match[1])) return null;
  return { kind: match[1] as UsagePointKind, id: Number(match[2]) };
}

export type UsagePeriod = 'm12' | 'm24' | 'this_year' | 'last_year';

/**
 * Inclusive date range (yyyy-mm-dd) for a period preset. The rolling presets
 * end today; the calendar-year presets cover the whole year, so "this year"
 * also counts bookings already planned for the rest of it.
 */
export function periodRange(period: UsagePeriod, today: Date = new Date()): { from: string; to: string } {
  const y = today.getFullYear();
  switch (period) {
    case 'this_year': return { from: `${y}-01-01`, to: `${y}-12-31` };
    case 'last_year': return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'm24':
    case 'm12': {
      const years = period === 'm24' ? 2 : 1;
      const from = new Date(y - years, today.getMonth(), today.getDate() + 1);
      return { from: isoDate(from), to: isoDate(today) };
    }
  }
}

function isoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * True when the browser holds Cognito tokens from an earlier sign-in, in
 * either storage Amplify may use (see lib/amplify.ts: Remember-me off keeps
 * them in sessionStorage). A hint only, used to decide whether the public map
 * shows the admin-only usage link. The admin page and its API still do the
 * real authentication and role check.
 */
export function hasStoredSession(storages: Storage[] = defaultStorages()): boolean {
  for (const storage of storages) {
    try {
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.LastAuthUser')) return true;
      }
    } catch {
      // Storage blocked (privacy mode): treat as signed out.
    }
  }
  return false;
}

function defaultStorages(): Storage[] {
  const out: Storage[] = [];
  try { out.push(window.localStorage); } catch { /* blocked */ }
  try { out.push(window.sessionStorage); } catch { /* blocked */ }
  return out;
}
